/**
 * /configuracion/resolucion — Gestión de la resolución DIAN.
 *
 * Muestra:
 *   - Estado de consumo en vivo (% del rango usado, días hasta vencer)
 *   - Alertas: ok / warning (≥80%) / critical (≥90% o vence en <30d) /
 *     exhausted (rango agotado, emisión bloqueada)
 *   - Form editable de la resolución activa
 *   - Botón "Cargar nueva resolución" — reset campos para nueva carga
 *
 * Notas enterprise:
 *   - Cargar nueva resolución es un cambio crítico: en la próxima
 *     iteración (J.3.2) lo migraremos a la tabla `taxor.dian_resolutions`
 *     con multi-version + audit log. Por ahora reutilizamos el PUT del
 *     profile.
 *   - El % consumido es calculado server-side leyendo MAX(doc_number)
 *     con el prefijo actual. Cuando los Cycles 2-7 entren (NC/ND/DS/POS),
 *     hay que actualizar el GetResolutionStatus para hacer UNION ALL.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  CloudDownload,
  FileWarning,
  KeyRound,
  Loader2,
  RefreshCw,
  Save,
} from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { profileApi, type UpsertProfileRequest } from '@/lib/api'
import { ROUTES } from '@/lib/constants'

type ResolutionForm = Pick<
  UpsertProfileRequest,
  | 'resolution_number'
  | 'resolution_prefix'
  | 'resolution_from'
  | 'resolution_to'
  | 'resolution_valid_from'
  | 'resolution_valid_to'
>

const EMPTY_RESOLUTION: ResolutionForm = {
  resolution_number: '',
  resolution_prefix: '',
  resolution_from: 0,
  resolution_to: 0,
  resolution_valid_from: '',
  resolution_valid_to: '',
}

export function ResolutionEditPage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState<ResolutionForm>(EMPTY_RESOLUTION)
  const [dirty, setDirty] = useState(false)
  const [isNewResolution, setIsNewResolution] = useState(false)

  const profile = useQuery({
    queryKey: ['settings-profile', activeOrgId],
    queryFn: () => profileApi.get(activeOrgId!),
    enabled: !!activeOrgId,
  })

  const status = useQuery({
    queryKey: ['resolution-status', activeOrgId],
    queryFn: () => profileApi.resolutionStatus(activeOrgId!),
    enabled: !!activeOrgId,
    refetchInterval: 60_000, // refrescar cada minuto.
  })

  // Hidrata el form con la resolución actual cuando llega el profile.
  useEffect(() => {
    if (profile.data && !dirty && !isNewResolution) {
      setForm({
        resolution_number: profile.data.resolution_number ?? '',
        resolution_prefix: profile.data.resolution_prefix ?? '',
        resolution_from: profile.data.resolution_from ?? 0,
        resolution_to: profile.data.resolution_to ?? 0,
        resolution_valid_from: profile.data.resolution_valid_from ?? '',
        resolution_valid_to: profile.data.resolution_valid_to ?? '',
      })
    }
  }, [profile.data, dirty, isNewResolution])

  const save = useMutation({
    mutationFn: () => {
      if (!profile.data) throw new Error('Profile no cargado')
      // PUT requiere el body COMPLETO del profile: preservamos todo lo
      // que no es resolución y solo updateamos los campos de resolución.
      const body: UpsertProfileRequest = {
        legal_name: profile.data.legal_name,
        trade_name: profile.data.trade_name,
        nit_verification_digit: profile.data.nit_verification_digit,
        address: profile.data.address,
        city: profile.data.city,
        city_dane_code: profile.data.city_dane_code,
        department: profile.data.department,
        department_dane_code: profile.data.department_dane_code,
        country: profile.data.country,
        postal_code: profile.data.postal_code,
        ciiu: profile.data.ciiu,
        ciiu_secondary: profile.data.ciiu_secondary,
        fiscal_responsibilities: profile.data.fiscal_responsibilities,
        tax_regime: profile.data.tax_regime,
        logo_url: profile.data.logo_url,
        primary_color: profile.data.primary_color,
        software_id: profile.data.software_id,
        // Resolución (lo nuevo / editado).
        resolution_number: form.resolution_number,
        resolution_prefix: form.resolution_prefix,
        resolution_from: form.resolution_from,
        resolution_to: form.resolution_to,
        resolution_valid_from: form.resolution_valid_from,
        resolution_valid_to: form.resolution_valid_to,
        // resolution_technical_key se omite: la resuelve el backend
        // contra DIAN (Taxor-managed, md/009).
      }
      return profileApi.upsert(activeOrgId!, body)
    },
    onSuccess: () => {
      toast.success(
        isNewResolution
          ? 'Nueva resolución cargada — la emisión continúa con el nuevo rango'
          : 'Resolución actualizada',
      )
      qc.invalidateQueries({ queryKey: ['settings-profile', activeOrgId] })
      qc.invalidateQueries({ queryKey: ['resolution-status', activeOrgId] })
      setDirty(false)
      setIsNewResolution(false)
      nav(ROUTES.SETTINGS, { replace: true })
    },
    onError: (e: Error) => {
      toast.error(`Error: ${e.message}`)
    },
  })

  // Sincronización server-side: Taxor consulta GetNumberingRange a DIAN
  // como PT, elige el rango vigente y persiste resolución + clave técnica.
  const syncFromDian = useMutation({
    mutationFn: () => profileApi.syncRanges(activeOrgId!),
    onSuccess: (res) => {
      toast.success(
        res.ranges.source === 'habilitacion'
          ? 'Resolución del ambiente de pruebas DIAN aplicada'
          : `Resolución sincronizada desde DIAN (${res.ranges.ranges.length} rango(s) detectados)`,
      )
      qc.invalidateQueries({ queryKey: ['settings-profile', activeOrgId] })
      qc.invalidateQueries({ queryKey: ['resolution-status', activeOrgId] })
      setDirty(false)
      setIsNewResolution(false)
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(
        `No se pudo sincronizar: ${e?.response?.data?.message ?? e?.message ?? 'error'}`,
      )
    },
  })

  function update<K extends keyof ResolutionForm>(key: K, value: ResolutionForm[K]) {
    setForm((f) => ({ ...f, [key]: value }))
    setDirty(true)
  }

  function startNewResolution() {
    setIsNewResolution(true)
    setDirty(true)
    setForm(EMPTY_RESOLUTION)
  }

  function cancelNewResolution() {
    setIsNewResolution(false)
    setDirty(false)
    // Recarga form con datos del profile.
    if (profile.data) {
      setForm({
        resolution_number: profile.data.resolution_number ?? '',
        resolution_prefix: profile.data.resolution_prefix ?? '',
        resolution_from: profile.data.resolution_from ?? 0,
        resolution_to: profile.data.resolution_to ?? 0,
        resolution_valid_from: profile.data.resolution_valid_from ?? '',
        resolution_valid_to: profile.data.resolution_valid_to ?? '',
      })
    }
  }

  if (authLoading || profile.isLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando resolución...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa.
        </div>
      </AppShell>
    )
  }

  if (profile.isError) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          No pude cargar el perfil.
        </div>
      </AppShell>
    )
  }

  // Validación cliente-side. La clave técnica NO se valida aquí: es
  // Taxor-managed (md/009), el backend la resuelve al guardar.
  const canSave =
    /^[A-Z0-9-]{1,10}$/.test(form.resolution_prefix.trim()) &&
    form.resolution_from > 0 &&
    form.resolution_to > form.resolution_from &&
    form.resolution_valid_from.length === 10 &&
    form.resolution_valid_to.length === 10 &&
    form.resolution_valid_to > form.resolution_valid_from &&
    form.resolution_number.trim().length > 0

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center gap-2">
          <Link
            to={ROUTES.SETTINGS}
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg-primary"
          >
            <ArrowLeft className="h-4 w-4" /> Configuración
          </Link>
        </div>
        <h1 className="mt-2 text-3xl font-bold">Resolución DIAN</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Rango de numeración autorizado por DIAN para emitir facturas
          electrónicas. Cuando se agota o vence, debes solicitar una nueva.
        </p>

        {/* Estado de consumo */}
        {status.data && <StatusCard data={status.data} />}

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (canSave && !save.isPending) save.mutate()
          }}
          className="mt-6 rounded-xl border border-border bg-bg-surface p-6 space-y-4"
        >
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {isNewResolution ? 'Cargar nueva resolución' : 'Resolución activa'}
            </h2>
            {!isNewResolution && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => syncFromDian.mutate()}
                  disabled={syncFromDian.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md border border-brand-500/40 bg-brand-500/10 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                  title="Taxor consulta tus rangos vigentes directamente a la DIAN y aplica el mejor (incluida la clave técnica, sin digitación)."
                >
                  {syncFromDian.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CloudDownload className="h-3.5 w-3.5" />
                  )}{' '}
                  Sincronizar desde DIAN
                </button>
                <button
                  type="button"
                  onClick={startNewResolution}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-app px-3 py-1.5 text-xs font-medium text-fg-secondary hover:bg-bg-elevated"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Captura manual
                </button>
              </div>
            )}
            {isNewResolution && (
              <button
                type="button"
                onClick={cancelNewResolution}
                className="text-xs text-fg-secondary hover:text-fg-primary"
              >
                Cancelar
              </button>
            )}
          </header>

          {isNewResolution && (
            <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800">
              <strong>Cargando nueva resolución:</strong> esta acción reemplaza
              la resolución activa. Los documentos ya emitidos conservan su
              número y prefijo originales. Los próximos a emitir usarán el
              nuevo rango.
            </div>
          )}

          <Field label="Número de resolución *" required help="Ej: 18760000001 (sin espacios).">
            <input
              type="text"
              value={form.resolution_number}
              onChange={(e) => update('resolution_number', e.target.value)}
              required
              className={`${input} font-mono`}
              placeholder="18760000001"
            />
          </Field>
          <Field
            label="Prefijo *"
            required
            help="Texto que precede al consecutivo (e.g. FE para facturas electrónicas)."
          >
            <input
              type="text"
              value={form.resolution_prefix}
              onChange={(e) =>
                update('resolution_prefix', e.target.value.toUpperCase().slice(0, 10))
              }
              required
              maxLength={10}
              className={`${input} font-mono w-32`}
              placeholder="FE"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Desde (número) *" required>
              <input
                type="number"
                min={1}
                value={form.resolution_from || ''}
                onChange={(e) => update('resolution_from', parseInt(e.target.value, 10) || 0)}
                required
                className={`${input} font-mono`}
                placeholder="1"
              />
            </Field>
            <Field label="Hasta (número) *" required>
              <input
                type="number"
                min={1}
                value={form.resolution_to || ''}
                onChange={(e) => update('resolution_to', parseInt(e.target.value, 10) || 0)}
                required
                className={`${input} font-mono`}
                placeholder="5000000"
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Vigencia desde *" required>
              <input
                type="date"
                value={form.resolution_valid_from}
                onChange={(e) => update('resolution_valid_from', e.target.value)}
                required
                className={input}
              />
            </Field>
            <Field label="Vigencia hasta *" required help="Típicamente 24 meses desde emisión.">
              <input
                type="date"
                value={form.resolution_valid_to}
                onChange={(e) => update('resolution_valid_to', e.target.value)}
                required
                className={input}
              />
            </Field>
          </div>
          {/* Clave técnica: Taxor-managed (md/009). DIAN la emite por
              rango y solo el PT puede consultarla — nunca se digita. */}
          <div className="flex items-start gap-3 rounded-md border border-border bg-bg-app p-3">
            <KeyRound className="mt-0.5 h-4 w-4 flex-none text-brand-600" />
            <div className="text-xs">
              <p className="font-semibold text-fg-primary">
                Clave técnica: la gestiona Taxor
              </p>
              <p className="mt-0.5 text-fg-secondary">
                Taxor consulta la clave técnica de tu rango directamente a la
                DIAN y la aplica al guardar. Estado actual:{' '}
                {profile.data?.resolution_technical_key ? (
                  <span className="font-mono">
                    {profile.data.resolution_technical_key.slice(0, 8)}… ✓
                  </span>
                ) : (
                  <span>pendiente de sincronizar</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <Link
              to={ROUTES.SETTINGS}
              className="text-sm text-fg-secondary hover:text-fg-primary"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={!canSave || save.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 px-5 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {save.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Guardando…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isNewResolution ? 'Cargar nueva resolución' : 'Guardar cambios'}
                </>
              )}
            </button>
          </div>
          {!canSave && (
            <p className="text-xs text-fg-muted">
              Completa los campos marcados con * y revisa las fechas.
            </p>
          )}
        </form>
      </div>
    </AppShell>
  )
}

// ─── Sub-componentes ─────────────────────────────────────────────────

const input =
  'mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20'

const ALERT_STYLES: Record<
  string,
  { bg: string; border: string; text: string; icon: typeof CheckCircle2 }
> = {
  ok: {
    bg: 'bg-success-tint',
    border: 'border-success/30',
    text: 'text-success-fg',
    icon: CheckCircle2,
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    border: 'border-yellow-300 dark:border-yellow-800',
    text: 'text-yellow-900 dark:text-yellow-100',
    icon: AlertTriangle,
  },
  critical: {
    bg: 'bg-error-tint',
    border: 'border-error/40',
    text: 'text-error-fg',
    icon: FileWarning,
  },
  exhausted: {
    bg: 'bg-error-tint',
    border: 'border-error/40',
    text: 'text-error-fg',
    icon: AlertCircle,
  },
}

function StatusCard({
  data,
}: {
  data: import('@/lib/api').ResolutionStatusResponse
}) {
  const style = ALERT_STYLES[data.alert_level] ?? ALERT_STYLES.ok
  const Icon = style.icon
  const pct = Math.min(100, Math.round(data.percent_consumed))

  return (
    <section
      className={`mt-6 rounded-xl border ${style.border} ${style.bg} p-5`}
    >
      <div className={`flex items-start gap-3 ${style.text}`}>
        <Icon className="h-5 w-5 flex-none mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold capitalize">
            {data.alert_level === 'ok' ? 'Resolución vigente' : data.alert_level}
          </p>
          {data.alert_reasons.length > 0 && (
            <ul className="mt-1 text-sm space-y-0.5">
              {data.alert_reasons.map((r, i) => (
                <li key={i}>• {r}</li>
              ))}
            </ul>
          )}

          {/* Barra de progreso */}
          <div className="mt-3">
            <div className="flex justify-between text-xs">
              <span>
                Consumidos:{' '}
                <strong className="font-mono">
                  {data.used_count.toLocaleString('es-CO')}
                </strong>{' '}
                de{' '}
                <span className="font-mono">
                  {data.range_size.toLocaleString('es-CO')}
                </span>
              </span>
              <span className="font-mono">{pct}%</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-bg-surface overflow-hidden border border-border">
              <div
                className={`h-full transition-all ${
                  data.alert_level === 'ok'
                    ? 'bg-success'
                    : data.alert_level === 'warning'
                      ? 'bg-yellow-500'
                      : 'bg-error'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Detalles */}
          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
            <Detail
              label="Prefijo"
              value={data.resolution_prefix}
              mono
            />
            <Detail
              label="Próximo número"
              value={`${data.resolution_prefix}${data.next_number}`}
              mono
            />
            <Detail
              label="Restantes"
              value={data.remaining_count.toLocaleString('es-CO')}
              mono
            />
            <Detail
              label="Vence en"
              value={
                data.is_expired
                  ? 'vencida'
                  : `${data.days_until_expiry} días`
              }
            />
          </dl>
        </div>
      </div>
    </section>
  )
}

function Detail({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div>
      <dt className="text-fg-muted">{label}</dt>
      <dd className={mono ? 'font-mono' : ''}>{value}</dd>
    </div>
  )
}

function Field({
  label,
  required,
  help,
  children,
}: {
  label: string
  required?: boolean
  help?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wide text-fg-secondary">
        {label}
        {required && <span className="ml-1 text-error-fg">*</span>}
      </label>
      {children}
      {help && <p className="mt-1 text-xs text-fg-muted">{help}</p>}
    </div>
  )
}
